import { useEffect, useState } from "react";
import { FaPlusSquare, FaCheck, FaPlus } from "react-icons/fa";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { TbBuildingPlus } from "react-icons/tb";
import JobInfoModal from "@modals/JobInfoModal";
import JobDescriptionModal from "@modals/JobDescriptionModal";
import EFListModal from "@modals/EFListModal";
import RequestModal from "@modals/RequestModal";
import HelpModal from "@modals/HelpModal";
import EditSiteModal from "@modals/EditSiteModal";
import Helpers from "@/Helpers";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import Enums from "@/enums";
import { copyUrlAsLink } from "@/utils/copyUrlAsLink";
import { beLinkOpensRequestModal } from "@/utils/beLinkRequest";
import { useI18n } from "@/i18n/I18nProvider";

const baseURL = Helpers.Env("VITE_TEST_RUNNER_URL");
const RECRUIT_SELECTED_JOB_SESSION_KEY = "RecruitSelectedJobId";

export default function JobList({ company, companyJobs, adminPerms, updateSpinner, refreshCompanyJobs, refreshRecruitDashboard, workflowItem, recruitDashboard, highlightJobTitleCells = false, highlightMissingLocationRows = false, highlightMissingJdRows = false, highlightMissingEfsRows = false, highlightKpiColorClass = null, onSelectedJobChange = null, legacyColumnLayout = false }) {
    const { t } = useI18n();
    const [jobInfoJobId, setJobInfoJobId] = useState(null);
    const [jobDescriptionJobId, setJobDescriptionJobId] = useState(null);
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [showJobsHelp, setShowJobsHelp] = useState(false);
    const [showAllJobs, setShowAllJobs] = useState(false);
    const [efListSiteJobId, setEfListSiteJobId] = useState(null);
    const [editSiteId, setEditSiteId] = useState(null);
    const [beRequest, setBeRequest] = useState(null);
    const [dragOverJdJobId, setDragOverJdJobId] = useState(null);
    const activeWFItem = workflowItem?.id;
    const activeDepartments = (companyJobs?.departments ?? []).filter((d) => d.active !== false);
    const showDepartmentColumn = legacyColumnLayout && company?.departmentsEnabled && activeDepartments.length > 0;
    const effectiveWFItem = activeWFItem;
    const showMissingBEsColumn = activeWFItem === "bis" || activeWFItem === "efs";
    const entityName = t(company?.subEntityTypeName || "Location");
    const entityPlural = t(Helpers.pluralize(company?.subEntityTypeName) || "Sites");
    const highlightColorByClass = {
        kpiRed: "rgb(252, 224, 220)",
        kpiGreen: "rgb(219, 244, 223)",
        kpiYellow: "rgb(255, 242, 222)",
        kpiBlack: "rgb(235, 237, 240)"
    };
    const highlightedBackgroundColor = highlightColorByClass[highlightKpiColorClass] || "rgb(207, 251, 207)";
    const selectedJobHasDescription = !!recruitDashboard?.jobs?.some((job) => job.id === selectedJobId && job.descCount > 0);

    useEffect(() => {
        if (!company?.id) return;
        try {
            const storedValue = window.sessionStorage.getItem(`${RECRUIT_SELECTED_JOB_SESSION_KEY}:${company.id}`);
            if (storedValue) {
                const parsedId = parseInt(storedValue, 10);
                if (!Number.isNaN(parsedId)) {
                    setSelectedJobId(parsedId);
                }
            }
        } catch {
        }
    }, [company?.id]);

    useEffect(() => {
        if (!company?.id) return;
        try {
            const storageKey = `${RECRUIT_SELECTED_JOB_SESSION_KEY}:${company.id}`;
            if (selectedJobId) {
                window.sessionStorage.setItem(storageKey, String(selectedJobId));
            } else {
                window.sessionStorage.removeItem(storageKey);
            }
        } catch {
        }
    }, [company?.id, selectedJobId]);

    useEffect(() => {
        if (!selectedJobId) return;
        if (!Array.isArray(recruitDashboard?.jobs)) return;
        const jobExists = recruitDashboard.jobs.some((job) => job.id === selectedJobId);
        if (!jobExists) {
            setSelectedJobId(null);
        }
    }, [recruitDashboard?.jobs, selectedJobId]);

    useEffect(() => {
        if (typeof onSelectedJobChange === "function") {
            onSelectedJobChange(selectedJobId || null);
        }
    }, [selectedJobId, onSelectedJobChange]);

    function handleJobColumnClick(e, companyJob, hasJobDescription, clickedColumn = "row") {
        if (!hasJobDescription && clickedColumn === "jd") {
            setSelectedJobId(companyJob.id);
            setJobDescriptionJobId(companyJob.id);
            return;
        }
        if (selectedJobId != companyJob.id)
            setSelectedJobId(companyJob.id);
        else if (canEditJobs())
            setJobInfoJobId(companyJob.id);
    }

    function isAdmin() {
        return adminPerms && adminPerms?.some(p => p === Enums.ROLE_PERMS.SystemAdmin);
    }

    const showActiveColumn = isAdmin();
    const showMissingBesColumn = !legacyColumnLayout && showMissingBEsColumn;

    function canEditJobs() {
        return isAdmin() || (adminPerms && adminPerms.includes(Enums.ROLE_PERMS.EditJobs));
    }

    function canAddJobs() {
        return isAdmin() || (adminPerms && adminPerms.includes(Enums.ROLE_PERMS.AddJob));
    }

    function canEditLocations() {
        return isAdmin() || (adminPerms && adminPerms.includes(Enums.ROLE_PERMS.EditSite));
    }

    function canCreateLocations() {
        return isAdmin() || (adminPerms && adminPerms.includes(Enums.ROLE_PERMS.CreateSite));
    }

    function canCreateBeSource() {
        return isAdmin() || companyJobs?.canCreateBeSource === true;
    }

    async function toggleJobSiteAsync(e) {
        const jobId = parseInt(e.currentTarget.getAttribute("data-jobid"));
        const siteId = parseInt(e.currentTarget.getAttribute("data-siteid"));
        if (jobId && siteId) {
            if (typeof updateSpinner === "function") {
                updateSpinner(true);
            }
            const resp = await RightTimeApi.putAsync("/JobToggleRelations", {
                jobId: jobId,
                siteIds: [siteId],
                companyId: company?.id
            });
            if (typeof updateSpinner === "function") {
                updateSpinner(false);
            }
            if (resp && resp.ok) {
                if (typeof refreshCompanyJobs === "function") {
                    await refreshCompanyJobs();
                }
                const job = recruitDashboard?.jobs?.find((item) => item.id === jobId);
                const site = companyJobs?.sites?.find((item) => item.id === siteId);
                toast.success(`Job ${job?.name || "job"} toggled for ${site?.alias || "site"}`);
            }
        }
    }

    async function toggleJobRoleAsync(e) {
        const jobId = parseInt(e.currentTarget.getAttribute("data-jobid"));
        const roleId = parseInt(e.currentTarget.getAttribute("data-roleid"));
        if (jobId && roleId) {
            if (typeof updateSpinner === "function") {
                updateSpinner(true);
            }
            const resp = await RightTimeApi.putAsync("/JobToggleRelations", {
                jobId,
                roleIds: [roleId],
                companyId: company?.id,
            });
            if (typeof updateSpinner === "function") {
                updateSpinner(false);
            }
            if (resp?.ok) {
                if (typeof refreshCompanyJobs === "function") {
                    await refreshCompanyJobs();
                }
                const job = companyJobs?.jobs?.find((item) => item.id === jobId);
                const role = companyJobs?.roles?.find((item) => item.roleId === roleId);
                toast.success(`Job ${job?.name || "job"} toggled for ${role?.name || "role"}`);
            }
        }
    }

    async function copyTextToClipboard(text, successMessage = "URL copied to clipboard", displayText?: string) {
        if (!text) {
            toast.error("No URL to copy");
            return false;
        }

        if (!window.isSecureContext) {
            console.warn("Clipboard API blocked: not in a secure context (HTTPS/localhost).");
        }

        try {
            const copied = await copyUrlAsLink(text, displayText);
            if (copied) {
                toast.success(successMessage);
                return true;
            }
        } catch (err) {
            console.warn("copyUrlAsLink failed:", err);
        }

        toast.error("Could not copy URL to clipboard");
        return false;
    }

    async function uploadJdFileForJobAsync(jobId, file, jobName) {
        if (!jobId || !file) return;

        try {
            if (typeof updateSpinner === "function") {
                updateSpinner(true);
            }

            const fileResp = await RightTimeApi.postFileAsync(`/Job/${jobId}/Description/File`, file);

            if (fileResp && fileResp.ok) {
                toast.success(`Description file uploaded for ${jobName || "job"}`);
                if (typeof refreshCompanyJobs === "function") {
                    await refreshCompanyJobs();
                }
                if (typeof refreshRecruitDashboard === "function") {
                    await refreshRecruitDashboard();
                }
            } else {
                toast.error("Failed to save description file.");
            }
        } catch (error) {
            console.error("Error uploading description file:", error);
            toast.error("Error saving description file.");
        } finally {
            if (typeof updateSpinner === "function") {
                updateSpinner(false);
            }
            setDragOverJdJobId(null);
        }
    }

    async function toggleJobActiveAsync(e, job) {
        e.stopPropagation();
        if (!canEditJobs()) return;

        const companyJobId = job?.companyJobId ?? job?.id;
        if (!companyJobId) return;

        if (typeof updateSpinner === "function") {
            updateSpinner(true);
        }

        const resp = await RightTimeApi.putAsync(`/Job/${companyJobId}/ToggleActive`);

        if (typeof updateSpinner === "function") {
            updateSpinner(false);
        }

        if (resp?.ok) {
            if (typeof refreshCompanyJobs === "function") {
                await refreshCompanyJobs();
            }
            if (typeof refreshRecruitDashboard === "function") {
                await refreshRecruitDashboard();
            }
            toast.success(`Job ${job?.name || "job"} is now ${(job?.active ?? true) ? "inactive" : "active"}`);
        }
    }

    function renderJobRows() {
        if (!companyJobs || !recruitDashboard) return null;
        const allJobs = Array.isArray(recruitDashboard.jobs) ? recruitDashboard.jobs : [];
        const visibleJobs = showAllJobs ? allJobs : allJobs.filter((job) => (job.active ?? true));
        return (<>
            <thead>
                <tr>
                    {adminPerms?.includes(254) && <th>ID</th>}
                    <th className={effectiveWFItem === "jobs" || effectiveWFItem === "algo" || effectiveWFItem === "questionnaires" ? "Name active" : "Name"}>
                        <span className="fieldTranslation" data-field-translation-key="jobList.jobsHeader.title">{t("Title")}</span>
                    </th>
                    {showDepartmentColumn ? (
                        <th>Department</th>
                    ) : null}
                    {showActiveColumn && (
                        <th
                            onClick={() => setShowAllJobs((prev) => !prev)}
                            data-help-id="ShowSelectedOrAll"
                            className={legacyColumnLayout ? "toggleColumnHeader" : undefined}
                            style={{ cursor: "pointer", minWidth: "70px", position: "relative" }}
                        >
                            <span className="toggleHeaderLabel fieldTranslation" data-field-translation-key={showAllJobs ? "jobList.jobsHeader.all" : "jobList.jobsHeader.active"}>{showAllJobs ? t("All") : t("Active")}</span>
                        </th>)}
                    {legacyColumnLayout ? (
                        <th data-help-id="jds" className={effectiveWFItem === "jds" || effectiveWFItem === "algo" ? "active" : ""}>JD</th>
                    ) : null}
                    <th data-help-id="jobSites" className={effectiveWFItem === "locations" ? "active" : ""}>
                        <span className="fieldTranslation" data-field-translation-key="jobList.jobsHeader.entityPlural">{t("{entityPlural}", "{entityPlural}", { entityPlural })}</span>
                    </th>
                    <th data-help-id="jobBis" className={effectiveWFItem === "bIs" || effectiveWFItem === "bis" || effectiveWFItem === "efs" ? "active" : ""}>
                        <span className="fieldTranslation" data-field-translation-key="jobList.jobsHeader.bes">{t("BEs")}</span>
                    </th>
                    {showMissingBesColumn ? (
                        <th data-help-id="jobBis" className={activeWFItem === "bIs" ? "active" : ""}>
                            <span className="fieldTranslation" data-field-translation-key="jobList.jobsHeader.missingBEs">{t("Missing BEs")}</span>
                        </th>
                    ) : null}
                </tr>
            </thead>
            <tbody>
                {visibleJobs.length > 0 ? (
                    visibleJobs.sort((a, b) => a.name.localeCompare(b.name)).map((job) => {
                        const hasJobDescription = job.descCount > 0;
                        const siteCount = companyJobs.siteJobs?.filter(sj => sj.jobId === job.id).length || 0;
                        const jobEfs = companyJobs.efTests?.filter(ef => ef.jobId === job.id) || [];
                        const uniqueSitesWithEfs = new Set(jobEfs.map(ef => ef.siteId));
                        const efSiteCount = uniqueSitesWithEfs.size;
                        const efTotalCount = jobEfs.length;
                        const highlightMissingLocation = highlightMissingLocationRows && siteCount === 0;
                        const highlightMissingJd = highlightMissingJdRows && job?.descCount === 0;
                        const highlightMissingEfs = highlightMissingEfsRows && efSiteCount === 0;
                        return (
                            <tr
                                key={"job" + job.id}
                                className={`jobRow${selectedJobId === job.id ? ' active' : ''}${siteCount === 0 ? ' missingLocation' : ''}${job?.descCount === 0 ? ' missingJD' : ''}${efSiteCount === 0 ? ' missingEFs' : ''}`.trim()}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => handleJobColumnClick(e, job, hasJobDescription, "row")}
                            >
                                {adminPerms?.includes(254) && (
                                    <td
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleJobColumnClick(e, job, hasJobDescription, "id");
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: (highlightMissingLocation || highlightMissingJd || highlightMissingEfs) ? highlightedBackgroundColor : undefined
                                        }}
                                    >
                                        {job.id}
                                    </td>
                                )}

                                <td
                                    className={`jobTitle ${effectiveWFItem === "jobs" || effectiveWFItem === "algo" || effectiveWFItem === "questionnaires" ? "Title active" : "Title"}${(!Array.isArray(job?.sites) || job.sites.length === 0) ? " missingSite" : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleJobColumnClick(e, job, hasJobDescription, "title");
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: (highlightMissingLocation || highlightMissingJd || highlightMissingEfs)
                                            ? highlightedBackgroundColor
                                            : (highlightJobTitleCells ? highlightedBackgroundColor : undefined)
                                    }}
                                    title={`${job.name} (${job.id})`}
                                >
                                    {job.name}
                                </td>
                                {showDepartmentColumn ? (
                                    <td
                                        className={`companyJobDepartmentCell${!(job.departmentIds?.length || job.departmentId) ? " missingDepartment" : ""}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleJobColumnClick(e, job, hasJobDescription, "title");
                                        }}
                                    >
                                        {job.departmentName || "—"}
                                    </td>
                                ) : null}
                                {showActiveColumn && (

                                    <td
                                        className="center activeToggleCell"
                                        onClick={(e) => toggleJobActiveAsync(e, job)}
                                        data-help-id="toggleActiveJob"
                                        style={{
                                            cursor: canEditJobs() ? 'pointer' : 'default',
                                            backgroundColor: (highlightMissingLocation || highlightMissingJd || highlightMissingEfs) ? highlightedBackgroundColor : undefined
                                        }}
                                    >
                                        {(job.active ?? true) ? (
                                            <MdCheckBox className={`checkbox checked actionIcon${canEditJobs() ? "" : " disabled"}`} />
                                        ) : (
                                            <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditJobs() ? "" : " disabled"}`} />
                                        )}
                                    </td>)}

                                {legacyColumnLayout ? (
                                    <td
                                        className="center"
                                        data-help-id="jds"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (hasJobDescription) {
                                                handleJobColumnClick(e, job, hasJobDescription, "jd");
                                            } else {
                                                setJobDescriptionJobId(job.id);
                                            }
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            backgroundColor: (highlightMissingLocation || highlightMissingJd || highlightMissingEfs) ? highlightedBackgroundColor : undefined
                                        }}
                                    >
                                        {hasJobDescription ? <FaCheck className="actionIcon jdCompleteCheck" /> : null}
                                    </td>
                                ) : null}

                                <td
                                    className="jobDescription"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleJobColumnClick(e, job, hasJobDescription, "locations");
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        backgroundColor: (highlightMissingLocation || highlightMissingJd || highlightMissingEfs) ? highlightedBackgroundColor : undefined
                                    }}
                                    data-help-id="jobSites"
                                >
                                    {(() => {
                                        return siteCount > 0 ? (
                                            <span className="badge" title={`${siteCount} site${siteCount !== 1 ? 's' : ''} associated`}>
                                                {siteCount}
                                            </span>
                                        ) : (
                                            <span className="badge" style={{ backgroundColor: 'red' }} title="No locations associated">
                                                0
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td
                                    className="siteCount"
                                    data-help-id="jobBis"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleJobColumnClick(e, job, hasJobDescription, "bis");
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        backgroundColor: (highlightMissingLocation || highlightMissingJd || highlightMissingEfs) ? highlightedBackgroundColor : undefined
                                    }}
                                >
                                    {(() => {
                                        const totalSiteCount = companyJobs.siteJobs?.filter(sj => sj.jobId === job.id).length || 0;

                                        let badgeColor;
                                        let tooltipText;

                                        if (efTotalCount === 0) {
                                            badgeColor = 'red';
                                            tooltipText = 'No BEs';
                                        } else if (efSiteCount < totalSiteCount) {
                                            badgeColor = 'orange';
                                            tooltipText = `${efTotalCount} BE${efTotalCount !== 1 ? 's' : ''} across ${efSiteCount} of ${totalSiteCount} location${totalSiteCount !== 1 ? 's' : ''}`;
                                        } else {
                                            badgeColor = undefined; // Use default blue
                                            tooltipText = `${efTotalCount} BE${efTotalCount !== 1 ? 's' : ''} across ${efSiteCount} site${efSiteCount !== 1 ? 's' : ''}`;
                                        }

                                        return (
                                            <span
                                                className="badge"
                                                style={{ backgroundColor: badgeColor }}
                                                title={tooltipText}
                                            >
                                                {efSiteCount}
                                            </span>
                                        );
                                    })()}
                                </td>
                                {showMissingBesColumn ? (
                                    <td
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleJobColumnClick(e, job, hasJobDescription, "bis");
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            backgroundColor: (highlightMissingLocation || highlightMissingJd || highlightMissingEfs) ? highlightedBackgroundColor : undefined
                                        }}
                                        data-help-id="jobBis"
                                    >
                                        {(() => {
                                            const activeSiteJobs = (companyJobs.siteJobs || []).filter(
                                                (sj) => sj.jobId === job.id && (sj.active ?? true)
                                            );
                                            const activeSiteIds = new Set(activeSiteJobs.map((sj) => sj.siteId));
                                            const sitesWithEfs = new Set(
                                                (companyJobs.efTests || [])
                                                    .filter((ef) => ef.jobId === job.id && activeSiteIds.has(ef.siteId))
                                                    .map((ef) => ef.siteId)
                                            );
                                            const missingEfCount = Math.max(0, activeSiteIds.size - sitesWithEfs.size);

                                            return (
                                                <span
                                                    className="badge"
                                                    style={{ backgroundColor: missingEfCount > 0 ? 'red' : undefined }}
                                                    title={`${missingEfCount} active site${missingEfCount !== 1 ? 's' : ''} without BEs`}
                                                >
                                                    {missingEfCount}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan={(() => {
                            let cols = 0;
                            if (adminPerms?.includes(254)) cols += 1;
                            if (showActiveColumn) cols += 1;
                            cols += 1; // title
                            if (legacyColumnLayout) cols += 1; // JD
                            cols += 1; // locations
                            cols += 1; // BEs
                            if (showMissingBesColumn) cols += 1;
                            return cols;
                        })()} className="center">
                            {" "}
                            <span className="fieldTranslation" data-field-translation-key="jobList.noJobsCreated">{t("No jobs have been created")}</span>{" "}
                        </td>
                    </tr>
                )}
            </tbody>
        </>);
    }

    return (
        <div className="jobList">
            <div className="jobListLayout">
                <div className="jobPrimaryColumn">
                    <div className={`tableWrapper dimensionTable scrollable full-height companyJobs jobsTable${legacyColumnLayout ? " horizontal" : ""}`}>
                        <table>
                            <caption data-help-id="jobsCaption">
                                <span className="title fieldTranslation" data-field-translation-key="jobList.jobsCaption">{t("Jobs")}</span>
                                <span className="captionRight" data-help-id="addJobs">
                                    <FaPlusSquare
                                        className={`actionIcon plusIcon${canAddJobs() ? "" : " disabled"}`}
                                        title={canAddJobs() ? t("Add Job") : t("You don't have permission to add jobs")}
                                        data-help-id="addJobs"
                                        onClick={() => {
                                            if (canAddJobs()) {
                                                setJobInfoJobId(0);
                                            }
                                        }}
                                    />
                                </span>
                            </caption>
                            {renderJobRows()}
                        </table>
                    </div>
                </div>

                <div className="jobSecondaryColumn">
                    <div className="tableWrapper factTable horizontal jobLocationsTable">
                        <table>
                            <caption data-help-id="jobLocationsCaption">
                                <span className="title fieldTranslation" data-field-translation-key="jobList.jobLocationsCaption">
                                    {t("{jobName} {entityName}", "{jobName} {entityName}", {
                                        jobName: (recruitDashboard?.jobs || []).find((job) => job.id === selectedJobId)?.name || "Job",
                                        entityName,
                                    })}
                                </span>
                                {canEditJobs() && (

                                    <span className="captionRight" data-help-id="addLocation">
                                        <FaPlusSquare
                                            className={`actionIcon plusIcon${canCreateLocations() ? "" : " disabled"}`}
                                            data-help-id="addLocation"
                                            title={
                                                canCreateLocations()
                                                    ? t("Add {entityName}", "Add {entityName}", { entityName })
                                                    : t("You don't have permission to add locations")
                                            }
                                            onClick={() => {
                                                if (canCreateLocations()) {
                                                    setEditSiteId(0);
                                                }
                                            }}
                                        />
                                    </span>)}
                            </caption>
                            <thead>
                                <tr>
                                    <th>
                                        <span className="fieldTranslation" data-field-translation-key="jobList.jobLocationsHeader.entityName">
                                            {t("{entityName}", "Site", { entityName })}
                                        </span>
                                    </th>
                                    {canEditJobs() || canEditLocations() ? (

                                        <th
                                            className={activeWFItem === "locations" ? "active" : undefined}
                                            data-help-id="jobLocationAccess"
                                        >
                                            <span className="toggleHeaderLabel fieldTranslation" data-field-translation-key="jobList.jobLocationsHeader.access">{t("Access")}</span>
                                        </th>) : null}
                                    <th className={effectiveWFItem === "bIs" || effectiveWFItem === "bis" || effectiveWFItem === "efs" ? "active" : ""}>
                                        <span className="fieldTranslation" data-field-translation-key="jobList.jobLocationsHeader.bes">{t("BEs")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {companyJobs?.sites && selectedJobId && companyJobs.sites.map((site) => {
                                    const isJobSiteActive = companyJobs.siteJobs && companyJobs.siteJobs.some((sj) => sj.siteId === site.id && sj.jobId === selectedJobId);
                                    const siteJobRecord = companyJobs.siteJobs?.find((sj) => sj.siteId === site.id && sj.jobId === selectedJobId);
                                    const efCode = siteJobRecord?.efTestCode;
                                    const siteJobId = siteJobRecord?.siteJobId;
                                    const matchingEfs = companyJobs.efTests?.filter((ef) => ef.jobId === selectedJobId && ef.siteId === site.id) || [];
                                    const efCount = matchingEfs.length;
                                    const highlightMissingEfRow = highlightMissingEfsRows && isJobSiteActive && efCount === 0;
                                    return (
                                        <tr key={`site${site.id}`} className={isJobSiteActive && efCount === 0 ? "missingEFs" : ""}>
                                            <th
                                                onClick={() => {
                                                    if (canEditLocations()) {
                                                        setEditSiteId(site.id);
                                                    }
                                                }}
                                                style={{
                                                    cursor: canEditLocations() ? "pointer" : "default",
                                                    backgroundColor: highlightMissingEfRow ? highlightedBackgroundColor : undefined
                                                }}
                                                title={
                                                    canEditLocations()
                                                        ? t("Edit {itemName}", "Edit {itemName}", {
                                                            itemName: site.alias || site.name || entityName,
                                                        })
                                                        : undefined
                                                }
                                            >
                                                {site.alias || site.name}
                                            </th>
                                            {canEditJobs() && (

                                                <td
                                                    className={effectiveWFItem === "locations" ? "actions active" : "actions"}
                                                    data-help-id="siteOpen"
                                                    data-jobid={selectedJobId}
                                                    data-siteid={site.id}
                                                    onClick={(e) => {
                                                        if (canEditJobs()) toggleJobSiteAsync(e);
                                                    }}
                                                    style={{
                                                        cursor: canEditJobs() ? "pointer" : "default",
                                                        backgroundColor: highlightMissingEfRow ? highlightedBackgroundColor : undefined
                                                    }}
                                                >
                                                    {isJobSiteActive ? (
                                                        <MdCheckBox className={`checkbox checked actionIcon${canEditJobs() ? "" : " disabled"}`} />
                                                    ) : (
                                                        <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditJobs() ? "" : " disabled"}`} />
                                                    )}
                                                </td>)}
                                            <td style={{ textAlign: "center", backgroundColor: highlightMissingEfRow ? highlightedBackgroundColor : undefined }}
                                                data-help-id="eflink"
                                                className={effectiveWFItem === "bIs" || effectiveWFItem === "efs" ? "active" : ""}>
                                                {isJobSiteActive && selectedJobHasDescription ? (
                                                    <TbBuildingPlus
                                                        className={`actionIcon plusIcon${canCreateBeSource() && efCode ? "" : " disabled"}`}
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            if (!canCreateBeSource()) {
                                                                return;
                                                            }
                                                            if (!efCode) {
                                                                toast.error("No BE code available for this job/site combination");
                                                                return;
                                                            }
                                                            if (beLinkOpensRequestModal(companyJobs)) {
                                                                setBeRequest({
                                                                    cttId: companyJobs.beSourceCompanyTestTypeId,
                                                                    siteId: site.id,
                                                                    jobId: selectedJobId,
                                                                });
                                                                return;
                                                            }
                                                            const url = baseURL + efCode;
                                                            await copyTextToClipboard(url);
                                                        }}
                                                        title={
                                                            canCreateBeSource()
                                                                ? (!efCode
                                                                    ? t("No BE code available")
                                                                    : beLinkOpensRequestModal(companyJobs)
                                                                        ? t("Enter additional BE fields")
                                                                        : t("Copy BE URL to clipboard"))
                                                                : t("You do not have permission to create a BE")
                                                        }
                                                    />
                                                ) : null}
                                                {isJobSiteActive || efCount > 0 ? (
                                                    <span
                                                        className="badge"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (efCount > 0) {
                                                                setEfListSiteJobId(siteJobId);
                                                            }
                                                        }}
                                                        style={{
                                                            cursor: efCount > 0 ? "pointer" : "default",
                                                            backgroundColor: efCount === 0 ? "red" : undefined
                                                        }}
                                                        title={efCount > 0 ? t("View BE list") : t("No BEs")}
                                                    >
                                                        {efCount}
                                                    </span>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {(!companyJobs?.sites || companyJobs.sites.length === 0) && (
                                    <tr>
                                        <td colSpan={3} className="center">
                                            <span className="fieldTranslation" data-field-translation-key="jobList.noEntityAvailable">
                                                {t("No {entityName} available", "No sites available", { entityName })}
                                            </span>
                                        </td>
                                    </tr>
                                )}
                                {!selectedJobId && (
                                    <tr>
                                        <td colSpan={3} className="center">
                                            <span className="fieldTranslation" data-field-translation-key="jobList.selectJobToViewAssignments">
                                                {t("Select a job to view {entityName} assignments", "Select a job to view site assignments", { entityName })}
                                            </span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {isAdmin() && (
                        <div className="tableWrapper factTable horizontal jobRolesTable">
                            <table>
                                <caption>
                                    <span className="title">Job / Roles</span>
                                </caption>
                                <thead>
                                    <tr>
                                        <th>Role</th>
                                        <th
                                            data-help-id="roleAccess"
                                        >
                                            <span className="toggleHeaderLabel">Access</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companyJobs?.roles && selectedJobId && companyJobs.roles.map((role) => {
                                        const isJobRoleActive = companyJobs.roleJobs && companyJobs.roleJobs.some((rj) => rj.roleId === role.roleId && rj.jobId === selectedJobId);
                                        return (
                                            <tr key={`role${role.roleId}`}>
                                                <th>{role.name}</th>
                                                <td
                                                    className="actions"
                                                    data-jobid={selectedJobId}
                                                    data-roleid={role.roleId}
                                                    onClick={(e) => {
                                                        if (canEditJobs()) toggleJobRoleAsync(e);
                                                    }}
                                                >
                                                    {isJobRoleActive ? (
                                                        <MdCheckBox className={`checkbox checked actionIcon${canEditJobs() ? "" : " disabled"}`} />
                                                    ) : (
                                                        <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditJobs() ? "" : " disabled"}`} />
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {(!companyJobs?.roles || companyJobs.roles.length === 0) && (
                                        <tr>
                                            <td colSpan={2} className="center">No roles available</td>
                                        </tr>
                                    )}
                                    {!selectedJobId && (
                                        <tr>
                                            <td colSpan={2} className="center">Select a job to view role assignments</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <HelpModal
                isOpen={showJobsHelp}
                onClose={() => setShowJobsHelp(false)}
                title="Jobs Table Help"
                content={
                    <>
                        <div className="helpSection">
                            <p>The Jobs table displays all positions you desire to fill.</p>
                            <ul>
                                <caption>Each Job must have the following:</caption>
                                <li>A Job Description (JD). A JD can be either a textual description or a file upload in a format of your choice.</li>
                                <li>At least one site/location</li>
                                <li>At least one BE Source test per site</li>
                            </ul>
                        </div>
                        <div className="helpSection">
                            <ul>
                                <caption>Actions</caption>
                                <li>Click <FaPlusSquare /> in the Jobs table header to create a new job</li>
                            </ul>
                        </div>
                    </>
                }
            />
            {jobInfoJobId != null && (
                <JobInfoModal
                    jobId={jobInfoJobId}
                    company={company}
                    adminPerms={adminPerms}
                    updateSpinner={updateSpinner}
                    onClose={async (result) => {
                        setJobInfoJobId(null);
                        const savedJobId = result?.savedJobId;
                        if (typeof refreshCompanyJobs === "function") {
                            await refreshCompanyJobs();
                        }
                        if (typeof refreshRecruitDashboard === "function") {
                            await refreshRecruitDashboard();
                        }
                        if (savedJobId) {
                            setSelectedJobId(savedJobId);
                        }
                    }}
                />
            )}
            <JobDescriptionModal
                jobId={jobDescriptionJobId}
                jobDescriptionId={0}
                updateSpinner={updateSpinner}
                onClose={async () => {
                    setJobDescriptionJobId(null);
                    if (typeof refreshCompanyJobs === "function") {
                        await refreshCompanyJobs();
                    }
                }}
            />
            <EFListModal
                siteJobId={efListSiteJobId}
                onClose={() => setEfListSiteJobId(null)}
                updateSpinner={updateSpinner}
            />
            <RequestModal
                isOpen={beRequest != null}
                title="Create Test"
                onClose={() => setBeRequest(null)}
                companyId={company?.id}
                company={company}
                updateSpinner={updateSpinner}
                cttId={beRequest?.cttId}
                lockedSiteId={beRequest?.siteId}
                lockedJobId={beRequest?.jobId}
                adminPerms={adminPerms}
            />
            {editSiteId !== null && (
                <EditSiteModal
                    isOpen={editSiteId !== null}
                    onClose={async () => {
                        setEditSiteId(null);
                        if (typeof refreshCompanyJobs === "function") {
                            await refreshCompanyJobs();
                        }
                    }}
                    siteId={editSiteId}
                    company={company}
                    updateSpinner={updateSpinner}
                    jobs={companyJobs?.jobs || []}
                    adminPerms={adminPerms}
                    {...(editSiteId === 0 && selectedJobId ? { defaultJobIds: [selectedJobId] } : {})}
                />
            )}
        </div>
    );
}

